import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import LocalizedLink from "@/components/LocalizedLink";
import PageHero from "@/components/PageHero";
import FormField from "@/pages/contact/FormField";
import PreferredReplyField from "@/pages/contact/PreferredReplyField";
import SuccessState from "@/pages/contact/SuccessState";
import { submitRequest } from "@/pages/contact/requestsApi";
import { GENERIC_STRINGS } from "@/pages/contact/requestFormsContent";
import { pickLang, useLanguage } from "@/i18n/LanguageContext";
import { trackEvent } from "@/analytics/ga";

/**
 * Shared form scaffold for all internal request types (Partnership,
 * Workshop, General Contact, Volunteer, Support, and Phase 24's V2
 * feedback).
 *
 * `spec` is one of the FORM objects from `requestFormsContent.js` and
 * describes intro text, admin label, and the ordered list of fields to
 * render.
 *
 * Phase 24 extensions (backwards-compatible — the 5 legacy forms don't
 * set any of these and behave exactly as before):
 *   - `spec.hidePageHero` — skip the PageHero when the form is embedded
 *     inside a custom page (e.g. `V2FeedbackRequest`).
 *   - `spec.contactOptional` — allow anonymous submission: the "how
 *     should we reply" question only becomes required after the user
 *     fills in one of the contact fields listed in
 *     `spec.contactFieldNames`.
 *   - `spec.contactFieldNames` — array of `field.name`s that count as
 *     "user gave us contact details" for the rule above.
 *   - `spec.eventNames` — override GA4 event names. Defaults to
 *     `contact_submit` (plus `workshop_request` alias for workshops).
 *     V2 feedback overrides to `v2_feedback_submit`.
 *   - `spec.suggestionCategoryField` — name of the field whose value
 *     is safe to include in GA4's `suggestion_category` parameter.
 *
 * We also read `?source=<v2_article|v2_banner>` from the URL and forward
 * it to the backend so admin can attribute submissions.
 */
export default function RequestForm({ spec }) {
  const { lang } = useLanguage();
  const g = GENERIC_STRINGS[lang];
  const [searchParams] = useSearchParams();
  const source = searchParams.get("source") || "";

  const initialValues = useMemo(
    () =>
      spec.fields.reduce((acc, f) => {
        // multiselect fields default to an empty array so React can
        // manage the toggle state without needing to coerce on each change.
        acc[f.name] = f.type === "multiselect" ? [] : "";
        return acc;
      }, {}),
    [spec]
  );

  const [values, setValues] = useState(initialValues);
  const [preferredReply, setPreferredReply] = useState("");
  // Honeypot — bots often fill every input; real users never touch this one.
  // Matches the backend's `_HONEYPOT_FIELD_NAME`.
  const [hpWebsite, setHpWebsite] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [success, setSuccess] = useState(false);

  // Convenience: "did the user provide any contact details in this
  // session?" — used both by the client-side validation (below) and by
  // the reply-method label (only starred when contact is present).
  const contactFieldNames = spec.contactFieldNames || [];
  const contactProvided =
    !spec.contactOptional ||
    contactFieldNames.some((n) => (values[n] || "").toString().trim());
  const replyRequired = !spec.contactOptional || contactProvided;

  // Reset local state whenever the user switches form type (edge case).
  useEffect(() => {
    setValues(initialValues);
    setPreferredReply("");
    setHpWebsite("");
    setErrors({});
    setServerError("");
    setSuccess(false);
  }, [initialValues, spec.key]);

  const setValue = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
    if (errors[name]) {
      setErrors((e) => {
        const next = { ...e };
        delete next[name];
        return next;
      });
    }
  };

  /** Mirror the backend's cheap client-side validation so users see issues fast. */
  const validate = () => {
    const next = {};
    for (const f of spec.fields) {
      const raw = values[f.name];
      // Multi-select: empty array is "no answer".
      if (f.type === "multiselect") {
        const arr = Array.isArray(raw) ? raw : [];
        if (arr.length === 0 && f.required) next[f.name] = g.required;
        continue;
      }
      const val = (raw || "").trim();
      if (!val) {
        if (f.required) next[f.name] = g.required;
        continue;
      }
      if (f.minLength && val.length < f.minLength) {
        next[f.name] = g.tooShort;
        continue;
      }
      if (f.type === "email" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(val)) {
        next[f.name] = g.invalidEmail;
        continue;
      }
    }
    // Reply method is required only when we know we might have to reply.
    if (replyRequired && !preferredReply) next.preferred_reply = g.required;
    return next;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    const clientErrors = validate();
    if (Object.keys(clientErrors).length) {
      setErrors(clientErrors);
      return;
    }
    setSubmitting(true);
    try {
      // Serialize field values into the shape the backend accepts.
      //   - multiselect stays as an array (backend accepts JSON arrays)
      //   - everything else is passed through unchanged
      const payloadFields = { ...values };

      await submitRequest({
        type: spec.key,
        language: lang,
        // Backend accepts empty preferred_reply for anonymous v2_feedback
        // — passing "" through is intentional in that case.
        preferredReply: replyRequired ? preferredReply : "",
        source,
        fields: payloadFields,
        honeypot: hpWebsite,
      });

      // Analytics.
      //   - Every submit fires one primary event (name overridable via spec.eventNames.submit).
      //   - The three legacy default cases (contact/workshop/etc.) fire
      //     `contact_submit` for parity with the Phase 23 wiring.
      //   - V2 forms fire `v2_feedback_submit` + include `source` and
      //     the safe `suggestion_category` value (whitelisted, no PII).
      const primaryEventName =
        (spec.eventNames && spec.eventNames.submit) || "contact_submit";
      const eventParams = {
        request_type: spec.key,
        language: lang,
      };
      if (source) eventParams.source = source;
      if (spec.suggestionCategoryField) {
        const catVal = values[spec.suggestionCategoryField];
        if (typeof catVal === "string" && catVal) {
          eventParams.suggestion_category = catVal;
        }
      }
      trackEvent(primaryEventName, eventParams);
      // Legacy alias so the workshop conversion event keeps working.
      if (spec.key === "workshop") {
        trackEvent("workshop_request", { language: lang });
      }

      setSuccess(true);
      // Scroll to top of the success panel so the confirmation is visible.
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (err) {
      if (err && err.status === 422 && err.fieldErrors) {
        // Surface server field errors (only relevant if client validation missed something).
        setErrors((prev) => ({ ...prev, ...err.fieldErrors }));
      } else if (err && err.status === 429) {
        setServerError(g.rateLimited || err.message || g.serverError);
      } else {
        setServerError(g.serverError);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetToFresh = () => {
    setValues(initialValues);
    setPreferredReply("");
    setHpWebsite("");
    setErrors({});
    setServerError("");
    setSuccess(false);
  };

  const title = pickLang(spec.title, lang);
  const intro = pickLang(spec.intro, lang);
  const eyebrow = pickLang(spec.eyebrow, lang);

  return (
    <>
      {!spec.hidePageHero && (
        <PageHero eyebrow={eyebrow} title={title} lead={intro} />
      )}
      <section
        className={spec.hidePageHero ? "sas-section-sm pt-0" : "sas-section-sm pt-0"}
        data-testid={`request-form-${spec.key}`}
      >
        <div className="sas-container">
          {!spec.hideBreadcrumb && (
            <nav
              className="text-xs text-[color:var(--sas-ink-2)] mb-6"
              aria-label={g.contactBreadcrumb}
            >
              <LocalizedLink to="/contact" className="hover:text-[color:var(--sas-accent)]">
                {g.contactBreadcrumb}
              </LocalizedLink>
              <span className="mx-2" aria-hidden>/</span>
              <span>{title}</span>
            </nav>
          )}

          <div className="max-w-2xl">
            {success ? (
              <SuccessState onSendAnother={resetToFresh} />
            ) : (
              <form
                onSubmit={onSubmit}
                noValidate
                className="flex flex-col gap-6 rounded-2xl border border-[color:var(--sas-line)] bg-white p-5 md:p-8"
                data-testid={`form-${spec.key}`}
              >
                {/* Anti-spam honeypot. Visually hidden (1px, clipped),
                    zero tab-stop, marked aria-hidden so real users (sighted
                    or using assistive tech) will never interact with it.
                    Automated bots that naively fill every text input will
                    get caught.

                    NOTE: we deliberately use the clip-based `sr-only`
                    technique instead of `left: -10000px`. In an RTL
                    document (`/ar/*`) a negative-left offset is *scrollable*
                    overflow, which produced ~10,000px of horizontal scroll on
                    every Arabic request form. `sr-only` never affects the
                    scrollable area in either direction. */}
                <div className="sr-only" aria-hidden="true">
                  <label htmlFor={`hp-${spec.key}`}>Website (leave blank)</label>
                  <input
                    id={`hp-${spec.key}`}
                    type="text"
                    name="hp_website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={hpWebsite}
                    onChange={(e) => setHpWebsite(e.target.value)}
                  />
                </div>
                {Object.keys(errors).length > 0 && (
                  <div
                    className="rounded-xl border border-[color:#fecaca] bg-[#fef2f2] px-4 py-3 text-[13.5px] text-[#991b1b]"
                    role="alert"
                    data-testid="form-error-summary"
                  >
                    {g.errorSummary}
                  </div>
                )}
                {serverError && (
                  <div
                    className="rounded-xl border border-[color:#fecaca] bg-[#fef2f2] px-4 py-3 text-[13.5px] text-[#991b1b]"
                    role="alert"
                    data-testid="form-server-error"
                  >
                    {serverError}
                  </div>
                )}

                {spec.fields.map((f) => (
                  <FormField
                    key={f.name}
                    field={f}
                    value={values[f.name]}
                    onChange={setValue}
                    error={errors[f.name]}
                  />
                ))}

                <PreferredReplyField
                  value={preferredReply}
                  optional={!replyRequired}
                  onChange={(v) => {
                    setPreferredReply(v);
                    if (errors.preferred_reply) {
                      setErrors((e) => {
                        const next = { ...e };
                        delete next.preferred_reply;
                        return next;
                      });
                    }
                  }}
                  error={errors.preferred_reply}
                  testIdPrefix={`${spec.key}-reply`}
                />

                <div className="flex flex-wrap items-center gap-3 pt-2">
                  <button
                    type="submit"
                    className="sas-btn sas-btn-primary"
                    disabled={submitting}
                    data-testid={`submit-${spec.key}`}
                  >
                    {submitting ? g.submitting : g.submit}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
